import { Injectable, Optional } from '@angular/core';

import _ from 'underscore';
import { cloneDeep } from 'lodash';
import moment from 'moment-timezone';

import { DataPointModel } from '../../Charts/models/data-point.model';
import { ChartModel } from '../../Charts/models/chart.model';
import { ReportBlockTypeEnum, reportBlockTypeEnumFromApi } from '../enums/report-block-type.enum';
import { reportsBlockMappingConfigurationMap, ReportsDataMappingKeysEnum } from '../models/reports-block-mapping-configuration.model';
import { MediaTypeEnum } from '../../Basic/enums/media-type.enum';
import { ReportBlockTypeApiEnum } from '../../../api/reports/enum/report-block-type-api.enum';
import { safeDivide } from '../../../Core/utils';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ChartMetricsEnum } from '../enums/chart-metrics.enum';
import { ExternalChartMetricsEnum } from '../enums/external-chart-metrics.enum';
import { GoogleAnalyticsReportTypesEnum } from '../../../api/externalData/enums/google-analytics-report-types.enum';
import { Am5Chart } from '../../Charts/models/am5-chart.model';
import { ChartSpecificTypesEnum } from '../enums/chart-specific-types.enum';
import { ReportSeriesModel } from '../models/report-series.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';


@Injectable()
export class ReportsSeriesDataMergerService {
    constructor(
        @Optional() private localizationService?: LocalizationService
    ) {
    }

    /**
     * Charts that have multiple sources / report series will get the results individually, while amchart data only expects one data array,
     * so this method functions as a transformer to align report series data with amchart
     * The input is an array of arrays of data points, e.g. [[dataPoints], [dataPonts], [dataPoints]],
     * The output is one single data point array, [[dataPoints]]
     */
    mergeSeriesData(
        data: DataPointModel[][],
        chart: ChartModel | Am5Chart,
        reportSeries?: ReportSeriesModel[]
    ): DataPointModel[][] {
        if (!data.every(x =>!!x)) {
            return data as DataPointModel[][];
        }

        let singleDataArray = data.flat();

        let chartType: ReportBlockTypeEnum, chartSpecificType: ChartSpecificTypesEnum;
        if (chart instanceof ChartModel) {
            chartType = chart.metadata.type;
            chartSpecificType = chart.options.chartSpecificType;
        } else if (chart instanceof Am5Chart) {
            chartType = chart.config.type;
            chartSpecificType = chart.settings.chartSpecificType;
        }

        const ogChartConfig = reportsBlockMappingConfigurationMap.get(chartType)
            || reportsBlockMappingConfigurationMap.get(reportBlockTypeEnumFromApi.get((chart as any).metadata.type as ReportBlockTypeApiEnum));
        if (!ogChartConfig) {
            return data as DataPointModel[][];
        }

        const chartConfig = JSON.parse(JSON.stringify(ogChartConfig));
        if (typeof ogChartConfig.mappingKey === 'function') {
            chartConfig.mappingKey = ogChartConfig.mappingKey(chartSpecificType);
        }
        const groupKey = chartConfig.groupKey || chartConfig.mappingKey;
        if (typeof ogChartConfig.categoryKey === 'function') {
            chartConfig.categoryKey = ogChartConfig.categoryKey(chartSpecificType);
        }

        if (chartConfig.filterEmptyDataValue) {
            const total = singleDataArray.reduce((a, b) => a + b.value, 0);
            if (!total) {
                return [];
            }
        }

        if (chartConfig.percentageAgainstSources) {
            this.calculateDataPointPercentageAgainstSources(singleDataArray, chartConfig.percentageGroupKey);
        }

        if (chartConfig.needsPercentage) {
            this.calculateDataPointPercentage(singleDataArray);
        }

        const categories = this.getCategoryOptions(
            chart,
            chartConfig.categoryKey,
            singleDataArray,
            reportSeries || (chart.series as ReportSeriesModel[])
        );

        // handles the case when some sources are long and some sources have mentions within 24 hours
        if (
            singleDataArray.some(x => x.timeScale === 'day')
            && singleDataArray.some(x => x.timeScale === 'hour')
            && chartConfig.categoryKey === ReportsDataMappingKeysEnum.SOURCE
        ) {
            categories.forEach((x, i) => {
                const groupedHourlyData = singleDataArray.filter(d => d.mappingKeys[chartConfig.categoryKey] === x && d.timeScale === 'hour');
                if (groupedHourlyData.length > 0) {
                    const dataPointGroups = Object.values(_.groupBy(groupedHourlyData, d => moment(d.mappingKeys['date'] as number).startOf('day').valueOf()));
                    dataPointGroups.forEach((group, index) => {
                        const dataPoint = cloneDeep(group[0]);
                        dataPoint.mappingKeys['date'] = moment(dataPoint.mappingKeys['date']).startOf('day').valueOf();
                        dataPoint['mentionCount'] = group.reduce((a, b) => a +(b['mentionCount'] as number), 0);
                        dataPoint['value'] = group.reduce((a, b) => a +(b['value'] as number), 0);
                        dataPoint['rawEndDate'] = group[group.length - 1]['rawEndDate'];
                        dataPoint['timeScale'] = 'day';
                        dataPoint['startTimeExport'] = dataPoint['startTimeExport'].split(' ')[0] + ' 00:00';
                        dataPoint['endTimeExport'] = moment(dataPoint['startTimeExport']).startOf('day')
                            .add(1, 'day').format('YYYY-MM-DD HH:mm:ss');
                        const searchCriteriaModel = MentionSearchCriteriaModel.fromFilterQueryString(dataPoint['viewUrl'].split('filters=')[1]);
                        searchCriteriaModel.search.query.dateQuery.absoluteRange.end = new Date(group[group.length - 1]['rawEndDate'] as number);
                        dataPoint['viewUrl'] = dataPoint['viewUrl'].split('filters=')[0] + 'filters='
                            + MentionSearchCriteriaModel.toFilterQueryString(searchCriteriaModel.search);
                        singleDataArray.push(dataPoint);
                    });
                }
            });
            singleDataArray = singleDataArray.filter(x => x.timeScale !== 'hour');
        }

        let groupedDataPoints = this.groupDataPointsByMappingKey(singleDataArray, groupKey);

        if (chartConfig.mappingKey === ReportsDataMappingKeysEnum.DATE) {
            groupedDataPoints = groupedDataPoints.sort((x, y) => {
                const dateX = x[0].mappingKeys?.['date'];
                const dateY = y[0].mappingKeys?.['date'];

                return dateX - dateY;
            });
        }

        let dataPoints = [];
        groupedDataPoints.forEach(group => {
            const dataPoint = JSON.parse(JSON.stringify(group[0]));
            dataPoint.key = dataPoint.mappingKeys?.[chartConfig.mappingKey];
            if (chart instanceof ChartModel) {
                if (!chart?.metadata?.ignoreForcingLabel) {
                    dataPoint.label = this.generateLabelForDataPoint(dataPoint, chartConfig, group, groupedDataPoints.length);
                }
            } else if (chart instanceof Am5Chart) {
                dataPoint.label = this.generateLabelForDataPoint(dataPoint, chartConfig, group, groupedDataPoints.length);
            }
            if (chartConfig.categoryKey) {
                categories.forEach((x, i) => {
                    const point = group.find(p => {
                        return p.mappingKeys?.[chartConfig.categoryKey] === x;
                    });
                    if (point) {
                        dataPoint['value' + i] = point.value;
                        dataPoint['secondaryValue' + i] = point.secondaryValue;
                        dataPoint['percent' + i] = point.percent;
                        dataPoint['viewUrl' + i] = point.viewUrl;
                        dataPoint['valueLabel' + i] = point.valueLabel;
                    } else if (chartConfig.categoryKey === ReportsDataMappingKeysEnum.DATE) {
                        dataPoint['value' + i] = 0;
                        dataPoint['percent' + i] = 0;
                        dataPoint['viewUrl' + i] = null;
                        dataPoint['valueLabel' + i] = null;
                    } else if (chartConfig.categoryKey === ReportsDataMappingKeysEnum.SOURCE) {
                        dataPoint['value' + i] = 0;
                        dataPoint['percent' + i] = 0;
                        dataPoint['viewUrl' + i] = null;
                        dataPoint['valueLabel' + i] = x;
                    }
                });
            } else {
                dataPoint['value0'] = dataPoint.value;
                dataPoint['percent0'] = dataPoint.percent;
                dataPoint['viewUrl0'] = dataPoint.viewUrl;
                dataPoint['valueLabel0'] = dataPoint.valueLabel;
            }
            if (chartConfig.sortByTotalDesc) {
                dataPoint['valueTotal'] = Object.keys(dataPoint).filter(x => x.match(/value\d+/g))
                    .reduce((x, y) => x + (dataPoint[y] || 0), 0);
            }

            dataPoints.push(dataPoint);
        });

        if (chartConfig.calculateStackedTotalPercent) {
            const total = dataPoints.reduce((x, y) => x + (y['valueTotal'] || 0), 0);
            dataPoints.forEach(x => {
                x['percentTotal'] = parseFloat((safeDivide(x['valueTotal'], total) * 100).toFixed(1));
                x['valueZero'] = 0;
            });
        }

        if (chartConfig.sortByTotalDesc) {
            dataPoints.sort((x, y) => y['valueTotal'] - x['valueTotal']);
        }

        if (typeof ogChartConfig.reverseDataPointOrder === 'function') {
            if (ogChartConfig.reverseDataPointOrder(chartSpecificType)) {
                dataPoints = dataPoints.reverse();
            }
        } else {
            if (ogChartConfig.reverseDataPointOrder) {
                dataPoints = dataPoints.reverse();
            }
        }
        if (chartConfig.mappingKey === ReportsDataMappingKeysEnum.DATE) {
            dataPoints = this.makeAllRangeSingleDayToHourlyInterval(dataPoints, categories, reportSeries);
        }

        return [dataPoints];
    }

    /**
     * When an 'All' date range only comes back with mentions that land inside a single day.
     * There is one hourly point so a line/area series has nothing to draw.
     * This fills in the empty hours and gives the same continuous 24 hour interval a 'last 24 hours' request would return
     */
    makeAllRangeSingleDayToHourlyInterval(
        dataPoints: DataPointModel[],
        categories: any[],
        reportSeries?: ReportSeriesModel[]
    ): DataPointModel[] {
        const isAllRange = reportSeries?.some(series => series?.dateRange?.type === DateQueryTypeEnum.ALL);
        if (!isAllRange || !dataPoints.length || !dataPoints.every(point => point.timeScale === 'hour')) {
            return dataPoints;
        }
        const lastKey = dataPoints[dataPoints.length - 1].key as number;
        if (lastKey - (dataPoints[0].key as number) >= 24 * 3600000) {
            return dataPoints;
        }
        // valueLabel is constant per category index (eg the metric or sentiment display name), not
        // per bucket - carry it over from a real bucket so the tooltip for a padded hour still
        // shows the series name instead of a blank label before the colon.
        const categoryCount = categories?.length || 1;
        const valueLabels: (string | undefined)[] = [];
        for (let i = 0; i < categoryCount; i++) {
            const withLabel = dataPoints.find(point => point['valueLabel' + i] != null);
            valueLabels[i] = withLabel ? withLabel['valueLabel' + i] as string : undefined;
        }
        const existingKeys = new Set(dataPoints.map(point => point.key as number));
        const padded = [...dataPoints];
        const end = moment(lastKey).add(1, 'hour');
        const cursor = end.clone().subtract(1, 'day').add(1, 'hour');
        while (cursor.isSameOrBefore(end)) {
            const key = cursor.valueOf();
            if (!existingKeys.has(key)) {
                const emptyPoint = {
                    key: key,
                    value: 0,
                    label: cursor.toDate().toTimeString().substring(0, 5),
                    rawStartDate: key,
                    rawEndDate: cursor.clone().add(1, 'hour').valueOf(),
                    timeScale: 'hour'
                } as DataPointModel;
                (categories?.length ? categories : [null]).forEach((x, i) => {
                    emptyPoint['value' + i] = 0;
                    emptyPoint['percent' + i] = 0;
                    emptyPoint['viewUrl' + i] = null;
                    emptyPoint['valueLabel' + i] = valueLabels[i];
                });
                padded.push(emptyPoint);
            }
            cursor.add(1, 'hour');
        }

        return padded.sort((x, y) => (x.key as number) - (y.key as number));
    }

    calculateDataPointPercentageAgainstSources(dataPoints: DataPointModel[], groupKey: ReportsDataMappingKeysEnum): void {
        const groupedDataPoints = this.groupDataPointsByMappingKey(dataPoints, groupKey);
        groupedDataPoints.forEach(group => {
            const total = group.reduce((x, y) => x + y.value, 0);
            group.forEach(dataPoint => {
                dataPoint.percent = parseFloat((safeDivide(dataPoint.value, total) * 100).toFixed(1));
            });
        });
        dataPoints = groupedDataPoints.flat();
    }

    calculateDataPointPercentage(dataPoints: DataPointModel[]): void {
        const total = dataPoints.reduce((sum, item) => sum + (+item.value || 0), 0);

        if (total === 0) {
            dataPoints.forEach(item => {
                item.percent = 0;
            });
            return;
        }

        dataPoints.forEach(item => {
            const value = +item.value || 0;
            item.percent = parseFloat((safeDivide(value, total) * 100).toFixed(1));
        });
    }


    groupDataPointsByMappingKey(dataPoints: DataPointModel[], mappingKey: ReportsDataMappingKeysEnum): DataPointModel[][] {
        const orderedKeys: string[] = [];

        const grouped = _.groupBy(dataPoints, x => {
            const key = x.mappingKeys?.[mappingKey];
            // use orderedKeys to ensure the order of the groups is the same as the order of the data points, since _.groupBy does not guarantee order
            if (!orderedKeys.includes(key)) {
                orderedKeys.push(key);
            }
            return key;
        });
        return orderedKeys.map(key => grouped[key]);
    }

    generateLabelForDataPoint(dataPoint: DataPointModel, chartConfig: any, group: DataPointModel[], dataGroupLength: number): string {
        switch (chartConfig.mappingKey) {
            case ReportsDataMappingKeysEnum.DATE:
                const date = dataPoint.mappingKeys?.[chartConfig.mappingKey];
                if (date < 2) {
                    // shared report does not work well with localization service, so hard coding this for now
                    if (date === 0 && dataGroupLength > 1) {
                        return 'Previous Period';
                    } else {
                        return 'Current Period';
                    }
                } else {
                    if (dataPoint.timeScale === 'hour') {
                        return moment(dataPoint.mappingKeys?.[chartConfig.mappingKey]).format('HH:mm');
                    } else if (dataPoint.timeScale === 'day') {
                        return moment(dataPoint.mappingKeys?.[chartConfig.mappingKey]).toDate().toDateString();
                    } else {
                        return `${moment(dataPoint.startTimeExport).format('ddd MMM D YYYY')} - ${moment(dataPoint.endTimeExport).format('ddd MMM D YYYY')}`;
                    }
                }
            case ReportsDataMappingKeysEnum.SOURCE:
            case ReportsDataMappingKeysEnum.AUTHOR:
            case ReportsDataMappingKeysEnum.OUTLET:
            case ReportsDataMappingKeysEnum.HASHTAG:
            case ReportsDataMappingKeysEnum.REFERRER:
            case ReportsDataMappingKeysEnum.REFERRER_TYPE:
                return dataPoint.mappingKeys?.[chartConfig.mappingKey];
            case ReportsDataMappingKeysEnum.SENTIMENT:
                if (this.localizationService) {
                    return this.localizationService.transform(`Sentiment.${dataPoint.mappingKeys?.[chartConfig.mappingKey]}`);
                } else {
                    return dataPoint.mappingKeys?.[chartConfig.mappingKey];
                }
            case ReportsDataMappingKeysEnum.MEDIA_TYPE:
                if (this.localizationService) {
                    return this.localizationService.transform(`MediaType.${dataPoint.mappingKeys?.[chartConfig.mappingKey]}`);
                } else {
                    return dataPoint.mappingKeys?.[chartConfig.mappingKey];
                }
            case ReportsDataMappingKeysEnum.COVERAGE_IMPACT_TREND_METRIC:
                if (this.localizationService) {
                    return this.localizationService.transform(`Reports.googleAnalyticsCharts.${dataPoint.mappingKeys?.[chartConfig.mappingKey]}`);
                } else {
                    return dataPoint.mappingKeys?.[chartConfig.mappingKey];
                }
            case ReportsDataMappingKeysEnum.LOCATION:
                return dataPoint.label;

        }
    }

    getCategoryOptions(
        chart: ChartModel | Am5Chart,
        categoryKey: ReportsDataMappingKeysEnum,
        singleDataArray: DataPointModel[],
        reportSeries?: ReportSeriesModel[]
    ): any[] {
        let fallbackMetric, fallbackMetrics, fallbackReferralTypes, fallbackSentiments;
        if (chart instanceof ChartModel) {
            fallbackMetric = chart.styles.metric;
            fallbackMetrics = chart.styles.metrics;
            fallbackReferralTypes = (chart.styles as any)?.referralTypes;
            fallbackSentiments = chart.styles.sentiments;
        }
        switch (categoryKey) {
            case ReportsDataMappingKeysEnum.SOURCE:
                return reportSeries.map(x => (x as any).label);
            case ReportsDataMappingKeysEnum.METRIC:
                return reportSeries[0]?.metrics || fallbackMetrics;
            case ReportsDataMappingKeysEnum.GA_METRIC:
                return this.getGACategoriesFromMetrics(reportSeries[0]?.metric || fallbackMetric);
            case ReportsDataMappingKeysEnum.REFERRER_TYPE:
                return reportSeries[0]?.referralTypes || fallbackReferralTypes;
            case ReportsDataMappingKeysEnum.COVERAGE_IMPACT_TREND_METRIC:
                const referralTypesOrder = [
                    GoogleAnalyticsReportTypesEnum.TOTAL_PAGEVIEWS,
                    GoogleAnalyticsReportTypesEnum.SESSIONS,
                    GoogleAnalyticsReportTypesEnum.USERS
                ];
                const referralTypes = (Array.from(reportSeries[1].referralTypes
                    || fallbackReferralTypes) as GoogleAnalyticsReportTypesEnum[]).sort((a, b) => {
                    return referralTypesOrder.indexOf(a) - referralTypesOrder.indexOf(b);
                });
                return [ChartMetricsEnum.volumeOfMentions, ...referralTypes];
            case ReportsDataMappingKeysEnum.SENTIMENT:
                return reportSeries[0].sentiments || fallbackSentiments;
            case ReportsDataMappingKeysEnum.MEDIA_TYPE:
                return reportSeries[0].styles.networkSelection || Object.values(MediaTypeEnum);
            case ReportsDataMappingKeysEnum.DATE:
                return [...new Set(singleDataArray.map(x => x.mappingKeys?.[categoryKey]))].sort();
            case ReportsDataMappingKeysEnum.PERIOD:
                return [0, 1];
        }
    }

    getGACategoriesFromMetrics(metric: ChartMetricsEnum): string[] {
        switch (metric) {
            case ExternalChartMetricsEnum.sessions:
                return [ExternalChartMetricsEnum.sessions];
            case ExternalChartMetricsEnum.sessionsNewReturning:
                return [ExternalChartMetricsEnum.sessionsNew, ExternalChartMetricsEnum.sessionsReturning];
            case ExternalChartMetricsEnum.users:
                return [ExternalChartMetricsEnum.users];
            case ExternalChartMetricsEnum.usersNewReturning:
                return [ExternalChartMetricsEnum.usersNew, ExternalChartMetricsEnum.usersReturning];
        }
    }
}
